import { z } from 'zod';

/**
 * Route params and query strings arrive as strings. Coercing here keeps `page`
 * from being compared as a string (`page > 1`) and a missing `limit` from
 * producing `NaN` and a silently wrong `skip`.
 */
export const idParamSchema = z.coerce.number().int().positive();

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

export const paginationQuerySchema = z.object({
  search: z.string().trim().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/** Offset for a 1-based page. */
export function toSkip({ page, limit }: PaginationQuery): number {
  return (page - 1) * limit;
}

/**
 * List payload. `count` is the number of rows matching the *same* filter as the
 * page — counting the whole table would make paging a search report a wrong
 * total.
 */
export interface Paginated<T> {
  count: number;
  items: T[];
}

/**
 * A boolean in a query string.
 *
 * Query strings are always strings, so booleans need an explicit decoder.
 * `z.coerce.boolean()` is the trap here: it applies JavaScript truthiness, so
 * the string `"false"` coerces to `true` and an unchecked filter chip — or a
 * `?header=false` print override — would silently stay on.
 */
export const queryBoolean = z
  .enum(['true', 'false', '1', '0'])
  .transform((value) => value === 'true' || value === '1');

/**
 * A comma-separated list of ids in a query string, e.g. `?report_ids=4,9,12`.
 *
 * Repeated `?report_ids=4&report_ids=9` is deliberately not supported: Express
 * hands that back as an array for two values and a bare string for one, so the
 * same request shape parses differently depending on how many the user picked.
 */
export const idListQuery = z
  .string()
  .transform((value) =>
    value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .map(Number),
  )
  // `Number('x')` is NaN, which fails `.int()` — so a malformed list is a 400
  // rather than a silently shorter selection.
  .pipe(z.array(z.number().int().positive()).min(1));

/**
 * `?download=true` on an endpoint that returns bytes.
 *
 * Absent means `inline`, which shows the document in the browser's own viewer;
 * `true` means `attachment`, which saves it. Every printable document takes
 * this — the rate list today, slips and procedures later — so it is declared
 * once rather than as a one-field schema per controller.
 */
export const downloadQuerySchema = z.object({ download: queryBoolean.optional() });
export type DownloadQuery = z.infer<typeof downloadQuerySchema>;

/** Money is Decimal(10,2) in Postgres and is serialised as a number over HTTP. */
export const moneySchema = z.coerce.number().nonnegative().multipleOf(0.01);
export const percentageSchema = z.coerce.number().min(0).max(100);

export const requiredString = (field: string) => z.string().trim().min(1, `${field} is required`);

/**
 * An optional free-text field: trimmed, and absent or null both mean "not set".
 *
 * Written out nine times across the entity schemas before this existed, which
 * is nine chances for one of them to forget the `.trim()` and store a field
 * that is whitespace on the way in and empty on the way out.
 */
export const optionalString = () => z.string().trim().nullish();

/**
 * An email address.
 *
 * The message is part of the contract: the client renders it under the input,
 * so it was repeated verbatim at all four declaration sites.
 */
export const emailSchema = z.email('Enter a valid email address');

/**
 * An email address that may be absent.
 *
 * `''` maps to `null` rather than failing validation: an empty text input posts
 * an empty string, and treating that as "you typed an invalid address" is a
 * form that refuses to submit with nothing visibly wrong on it.
 */
export const optionalEmail = () =>
  z
    .union([z.literal(''), emailSchema])
    .nullish()
    .transform((value) => value || null);

/**
 * A phone number.
 *
 * Deliberately unvalidated beyond being trimmed. Pakistani mobile, landline and
 * international formats all appear in this data, and a regex tight enough to be
 * worth having would reject real numbers at the front desk.
 */
export const phoneSchema = requiredString('Phone number');

/**
 * The fewest digits that make a phone number: `canonicalPhone` on the server
 * matches nothing shorter, so the client does not look one up either.
 */
export const MIN_PHONE_DIGITS = 7;

/** A phone number that may be absent. Blank means "not recorded", not "invalid". */
export const optionalPhone = () =>
  z
    .string()
    .trim()
    .nullish()
    .transform((value) => value || null);

/** A whole-year age. The upper bound is a typo guard, not a claim about people. */
export const ageYearsSchema = z.coerce.number().int().min(0).max(150);

/**
 * The name-and-code pair every catalogue entity is identified by.
 *
 * Spread into a field object: `{ ...nameAndCode, price: … }`.
 */
export const nameAndCode = {
  name: requiredString('Name'),
  code: requiredString('Code'),
};

/** The columns every stored row carries, as they arrive over the wire. */
export interface Timestamped {
  id: number;
  created_at: string;
  updated_at: string;
}
